/**
 * The one row a finished coding turn's work collapses into.
 *
 * `14 steps · 3m 12s ✓` — how much happened, how long it took, whether it
 * worked. That is the whole of what a reader needs from a turn they are
 * scrolling past; everything else is available by opening it.
 *
 * Deliberately quiet: muted text, no rule, no card, only as wide as its words.
 * The answer below it is the content, and a summary that draws attention to
 * itself — a full-width bar, a box — would compete with the thing it exists to
 * make readable.
 *
 * @module components/chat/messages/coding-turn-summary
 */

import { CheckIcon, ChevronRightIcon, XIcon } from "lucide-react";
import type { ReactNode } from "react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

export interface CodingTurnSummaryProps {
  /** Steps folded into this row, duplicates included. */
  stepCount: number;
  /** How long the turn took, already formatted. Omitted for older turns. */
  duration?: string;
  /** Whether the turn ended cleanly. */
  failed?: boolean;
  children: ReactNode;
}

export function CodingTurnSummary({
  stepCount,
  duration,
  failed = false,
  children,
}: CodingTurnSummaryProps) {
  return (
    /*
      `not-prose` sits on the trigger, never on the wrapper. The fold contains
      the narration the agent wrote between steps, which renders as markdown —
      suppressing prose on an ancestor stripped its typography and made an
      expanded turn read differently from the same turn while it streamed.
    */
    <Collapsible className="my-3 flex flex-col" defaultOpen={false}>
      <CollapsibleTrigger
        data-testid="coding-turn-summary-toggle"
        className={cn(
          "not-prose group/turn flex items-center gap-2 self-start text-sm text-muted-foreground",
          "transition-colors hover:text-foreground",
        )}
      >
        <ChevronRightIcon
          className={cn(
            "size-3.5 shrink-0 transition-transform",
            "group-data-[state=open]/turn:rotate-90",
          )}
        />
        <span className="shrink-0">
          {stepCount} {stepCount === 1 ? "step" : "steps"}
        </span>
        {duration !== undefined && (
          <>
            <span className="shrink-0 text-muted-foreground/60">·</span>
            <span className="shrink-0 tabular-nums">{duration}</span>
          </>
        )}
        {failed ? (
          <XIcon className="size-3.5 shrink-0 text-destructive" />
        ) : (
          <CheckIcon className="size-3.5 shrink-0 text-success" />
        )}
      </CollapsibleTrigger>

      <CollapsibleContent
        data-testid="coding-turn-summary"
        className={cn(
          "data-[state=closed]:animate-out data-[state=open]:animate-in",
          "data-[state=closed]:fade-out-0 data-[state=open]:slide-in-from-top-2",
        )}
      >
        {/*
          Expanding restores the turn exactly as it streamed — same rows, same
          indentation, same width. The fold is a way to hide the steps, not a
          different presentation of them, so there is no rail and no inset here:
          those would make an expanded turn read as a quoted aside rather than
          as the transcript it was a moment ago.
        */}
        <div className="flex flex-col">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}
